import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useEntityHistory } from "../../hooks/useEntityHistory";
import type { EntityActivity } from "../../hooks/useEntityHistory";
import type { TripWithId } from "../../hooks/useTrips";
import type { SegmentWithId } from "../../hooks/useSegments";
import { AnalyticsBar } from "../common/AnalyticsBar";
import { NotesDisplay } from "../common/NotesDisplay";
import { WhoWasThereList } from "../common/WhoWasThere";
import { PeopleDisplay } from "../interactions/PeopleEditor";
import {
  entityVisitStats,
  orderVisitsNewestFirst,
  partyKeysForUser,
  summarizeEntityHistory,
  visitParticipants,
} from "../../utils/entityHistory";
import type { EntityVisitStats } from "../../utils/entityHistory";
import {
  formatDateLabel,
  formatDuration,
  formatTimeRange,
  segmentDayLookup,
  segmentZoneLookup,
  activityZone,
  zoneNote,
} from "../../utils/tripDisplay";
import type { Zone } from "../../utils/tripDisplay";
import { segmentParticipantsLookup } from "../../utils/participants";
import type { CustomQuestion, DiningOrder, Entity, LoungeRound } from "../../types";
import { answeredQuestions } from "../../utils/customQuestions";
import { resolveCustomQuestions } from "../../config/typeSettings";
import { useTypeSettings } from "../../contexts/TypeSettingsContext";

interface EntityHistoryProps {
  entityId: string;
  entity: Entity;
}

/** A visit with everything the row needs already resolved. */
interface VisitRow {
  activity: EntityActivity;
  stats: EntityVisitStats;
  trip: TripWithId | undefined;
  segment: SegmentWithId | undefined;
  zone: Zone;
  participantIds: string[];
  /** The place's questions, so the visit can show what was answered. */
  questions: CustomQuestion[];
}

function minutes(mins: number): string {
  return `${Math.round(mins)}m`;
}

/** "Mar 3, 2025" — the year matters here, since visits span trips. */
function formatVisitDate(d: Date, zone: Zone): string {
  const year = d.toLocaleDateString(undefined, { year: "numeric", timeZone: zone });
  return `${formatDateLabel(d, zone)}, ${year}`;
}

function participantName(
  id: string,
  trip: TripWithId | undefined,
): string {
  if (id === "SHARED") return "Shared";
  return trip?.travelParty?.[id]?.displayName ?? id;
}

/**
 * What was ordered, as it was written down: flat orders as one list, rounds as
 * one list each. Round placed/received times are already folded into the
 * service-time stat, so only the food is repeated here.
 */
function OrdersSummary({ activity, trip }: { activity: EntityActivity; trip: TripWithId | undefined }) {
  const cr = activity.customResponses as Record<string, unknown> | undefined;
  const orders = (Array.isArray(cr?.orders) ? cr!.orders : []) as DiningOrder[];
  const rounds = (Array.isArray(cr?.rounds) ? cr!.rounds : []) as LoungeRound[];
  const named = (list: DiningOrder[]) => list.filter((o) => o.order && o.order.trim() !== "");
  const roundLists = rounds.map((r) => named(r.orders ?? [])).filter((l) => l.length > 0);
  const flat = named(orders);
  if (roundLists.length === 0 && flat.length === 0) return null;
  const renderList = (list: DiningOrder[]) => (
    <ul className="ehist-orders">
      {list.map((o, i) => (
        <li key={i}>
          <span className="ehist-order-who">{participantName(o.participantId, trip)}</span> {o.order}
        </li>
      ))}
    </ul>
  );
  return (
    <div className="ehist-block">
      <div className="ehist-block-label">Orders</div>
      {roundLists.length > 0
        ? roundLists.map((list, i) => (
            <div key={i} className="ehist-round">
              {roundLists.length > 1 && <div className="ehist-round-label">Round {i + 1}</div>}
              {renderList(list)}
            </div>
          ))
        : renderList(flat)}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "delay" }) {
  return (
    <div className={`ehist-stat${tone ? ` ehist-stat-${tone}` : ""}`}>
      <span className="ehist-stat-k">{label}</span>
      <span className="ehist-stat-v">{value}</span>
    </div>
  );
}

/** What the place asked and what was answered: "Song — Conga". */
function AnswersSummary({ activity, questions }: { activity: EntityActivity; questions: CustomQuestion[] }) {
  const answers = answeredQuestions(questions, activity.customResponses);
  if (answers.length === 0) return null;
  return (
    <div className="ehist-block">
      <dl className="ehist-answers">
        {answers.map(({ question, value }) => (
          <div key={question.id} className="ehist-answer">
            <dt>{question.label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function VisitCard({ row }: { row: VisitRow }) {
  const { activity, stats, trip, segment, zone, participantIds, questions } = row;
  const when = stats.start;
  const timeRange = stats.start && stats.end ? formatTimeRange(stats.start, stats.end, zone) : null;
  const zoneMark = when ? zoneNote(when, zone) : null;
  const hasStats =
    stats.postedWait != null ||
    stats.actualWaitSec > 0 ||
    stats.delaySec > 0 ||
    stats.durationSec > 0 ||
    stats.serviceSec > 0;

  return (
    <li className="ehist-visit">
      <div className="ehist-visit-head">
        <div className="ehist-visit-when">
          <span className="ehist-visit-date">{when ? formatVisitDate(when, zone) : "Undated"}</span>
          {timeRange && (
            <span className="ehist-visit-time">
              {timeRange}
              {zoneMark && <span className="ehist-zone"> {zoneMark}</span>}
            </span>
          )}
        </div>
        {stats.tierName && <span className="tl-badge">{stats.tierName}</span>}
      </div>
      <div className="ehist-visit-trip">
        {trip ? (
          <Link to={`/trips/${trip.id}`}>{trip.name}</Link>
        ) : (
          <span className="text-muted">Trip</span>
        )}
        {segment && (
          <>
            <span className="ehist-sep">·</span>
            <Link to={`/trips/${activity.tripId}/segments/${segment.id}`}>{segment.name}</Link>
          </>
        )}
        {activity.name && (
          <>
            <span className="ehist-sep">·</span>
            <span>{activity.name}</span>
          </>
        )}
      </div>

      {hasStats && (
        <div className="ehist-stats">
          {stats.postedWait != null && <Stat label="Posted" value={minutes(stats.postedWait)} />}
          {stats.expectedWait != null && <Stat label="Expected" value={minutes(stats.expectedWait)} />}
          {stats.actualWaitSec > 0 && <Stat label="Waited" value={formatDuration(stats.actualWaitSec)} />}
          {stats.delaySec > 0 && <Stat label="Delayed" value={formatDuration(stats.delaySec)} tone="delay" />}
          {stats.serviceSec > 0 && <Stat label="Service" value={formatDuration(stats.serviceSec)} />}
          {stats.durationSec > 0 && <Stat label="Total" value={formatDuration(stats.durationSec)} />}
        </div>
      )}

      {trip && participantIds.length > 0 && (
        <WhoWasThereList travelParty={trip.travelParty ?? {}} memberIds={participantIds} />
      )}

      {activity.people && activity.people.length > 0 && (
        <PeopleDisplay people={activity.people} />
      )}

      <AnswersSummary activity={activity} questions={questions} />

      <OrdersSummary activity={activity} trip={trip} />

      {activity.notes && (
        <div className="ehist-block">
          <NotesDisplay value={activity.notes} className="ehist-notes" />
        </div>
      )}

      <Link to={`/trips/${activity.tripId}/interactions/${activity.id}`} className="ehist-open">
        Open activity &rarr;
      </Link>
    </li>
  );
}

/**
 * Every visit the reader was part of at this entity, across their trips: the
 * headline numbers, how each access tier has actually performed, and each
 * visit with what was written down at the time.
 */
export function EntityHistory({ entityId, entity }: EntityHistoryProps) {
  const { user } = useAuth();
  const uid = user?.uid ?? null;
  const { activities, ancestorsById, tripsById, segmentsByTrip, loading, error } =
    useEntityHistory(entityId);
  const [showAll, setShowAll] = useState(false);
  const typeSettings = useTypeSettings(entity.type);

  const rows = useMemo<VisitRow[]>(() => {
    // Segment ids are Firestore auto-ids, unique across trips, so one day
    // lookup can serve every trip at once. Zones are per trip.
    const allSegments = Object.values(segmentsByTrip).flat();
    const onDay = segmentDayLookup(allSegments);
    const segmentById = new Map(allSegments.map((s) => [s.id, s]));
    const zoneByTrip = new Map<string, ReturnType<typeof segmentZoneLookup>>();
    const partyByTrip = new Map<string, ReturnType<typeof segmentParticipantsLookup>>();
    const questions = resolveCustomQuestions(entity, typeSettings);

    // Someone else's ride on a shared trip is readable but isn't a visit of
    // the reader's; only what they were part of counts here.
    const related = [...activities, ...Object.values(ancestorsById)];
    const built = activities.flatMap((activity) => {
      const trip = tripsById[activity.tripId];
      const tripSegments = segmentsByTrip[activity.tripId] ?? [];
      let onZone = zoneByTrip.get(activity.tripId);
      if (!onZone) {
        onZone = segmentZoneLookup(tripSegments, trip?.timezone ?? undefined);
        zoneByTrip.set(activity.tripId, onZone);
      }
      const memberIds = Object.keys(trip?.travelParty ?? {});
      let onParty = partyByTrip.get(activity.tripId);
      if (!onParty) {
        onParty = segmentParticipantsLookup(tripSegments, memberIds);
        partyByTrip.set(activity.tripId, onParty);
      }
      const participantIds = visitParticipants(activity, related, onParty, memberIds);
      const userKeys = partyKeysForUser(trip?.travelParty, uid);
      if (!userKeys.some((k) => participantIds.includes(k))) return [];
      const stats = entityVisitStats(activity, entity.activityOptions, onDay);
      return [{
        activity,
        stats,
        trip,
        segment: activity.segmentId ? segmentById.get(activity.segmentId) : undefined,
        zone: activityZone(activity, onZone),
        participantIds,
        questions,
      }];
    });
    return orderVisitsNewestFirst(built.map((r) => ({ row: r, start: r.stats.start }))).map((x) => x.row);
  }, [activities, ancestorsById, tripsById, segmentsByTrip, entity, typeSettings, uid]);

  const summary = useMemo(() => summarizeEntityHistory(rows.map((r) => r.stats)), [rows]);

  if (loading) {
    return (
      <section className="entity-history">
        <h2>History</h2>
        <div className="loading">Loading...</div>
      </section>
    );
  }

  if (rows.length === 0) {
    return (
      <section className="entity-history">
        <h2>History</h2>
        <p className="text-muted">
          {error ? "Couldn't load some of your trips." : "You haven't been here on any of your trips yet."}
        </p>
      </section>
    );
  }

  const lastZone = rows[0]?.zone;
  const maxTier = Math.max(
    1,
    ...summary.tiers.flatMap((t) => [t.avgPosted ?? 0, (t.avgActual ?? 0) / 60, t.expected ?? 0]),
  );
  const hasExpected = summary.tiers.some((t) => t.expected != null);
  const VISIBLE = 5;
  const visible = showAll ? rows : rows.slice(0, VISIBLE);

  return (
    <section className="entity-history">
      <h2>History</h2>
      {error && <p className="form-error">Some trips couldn't be loaded; this may be incomplete.</p>}

      <div className="analytics-grid">
        <div className="analytics-card">
          <span className="analytics-card-value">{summary.visits}</span>
          <span className="analytics-card-label">{summary.visits === 1 ? "Visit" : "Visits"}</span>
        </div>
        {summary.lastVisit && (
          <div className="analytics-card">
            <span className="analytics-card-value analytics-card-value-date">
              {formatVisitDate(summary.lastVisit, lastZone)}
            </span>
            <span className="analytics-card-label">Last Visit</span>
          </div>
        )}
        {summary.firstVisit && summary.visits > 1 && (
          <div className="analytics-card">
            <span className="analytics-card-value analytics-card-value-date">
              {formatVisitDate(summary.firstVisit, rows[rows.length - 1]?.zone)}
            </span>
            <span className="analytics-card-label">First Visit</span>
          </div>
        )}
        {summary.avgPostedWait != null && (
          <div className="analytics-card">
            <span className="analytics-card-value">{minutes(summary.avgPostedWait)}</span>
            <span className="analytics-card-label">Avg Posted</span>
          </div>
        )}
        {summary.avgActualWait != null && (
          <div className="analytics-card">
            <span className="analytics-card-value">{formatDuration(summary.avgActualWait)}</span>
            <span className="analytics-card-label">Avg Waited</span>
          </div>
        )}
        {summary.avgService != null && (
          <div className="analytics-card">
            <span className="analytics-card-value">{formatDuration(summary.avgService)}</span>
            <span className="analytics-card-label">Avg Service</span>
          </div>
        )}
        {summary.avgDuration != null && (
          <div className="analytics-card">
            <span className="analytics-card-value">{formatDuration(summary.avgDuration)}</span>
            <span className="analytics-card-label">Avg Time Here</span>
          </div>
        )}
        {summary.totalDelay > 0 && (
          <div className="analytics-card analytics-card-delay">
            <span className="analytics-card-value">{formatDuration(summary.totalDelay)}</span>
            <span className="analytics-card-label">Total Delay</span>
          </div>
        )}
      </div>

      {summary.tiers.length > 0 && (
        <div className="analytics-section">
          <h3>By Access Tier</h3>
          <div className="analytics-legend">
            <span className="analytics-legend-item">
              <span className="abar-swatch abar-posted" /> Posted
            </span>
            {hasExpected && (
              <span className="analytics-legend-item">
                <span className="abar-swatch abar-expected" /> Expected
              </span>
            )}
            <span className="analytics-legend-item">
              <span className="abar-swatch abar-actual" /> Waited
            </span>
          </div>
          <div className="abar-list">
            {summary.tiers.map((t) => (
              <div key={t.name} className="abar-group">
                <span className="abar-name">
                  {t.name}{" "}
                  <span className="abar-name-meta">
                    · {t.count === 1 ? "1 time" : `${t.count} times`}
                    {t.lastVisit && <> · last {formatVisitDate(t.lastVisit, lastZone)}</>}
                  </span>
                </span>
                {t.avgPosted != null && (
                  <AnalyticsBar value={t.avgPosted} max={maxTier} label={`${t.avgPosted.toFixed(1)}m`} tone="posted" />
                )}
                {t.expected != null && (
                  <AnalyticsBar value={t.expected} max={maxTier} label={`${t.expected.toFixed(1)}m`} tone="expected" />
                )}
                {t.avgActual != null && (
                  <AnalyticsBar value={t.avgActual / 60} max={maxTier} label={`${(t.avgActual / 60).toFixed(1)}m`} tone="actual" />
                )}
                {t.avgPosted == null && t.avgActual == null && (
                  <span className="text-muted ehist-tier-empty">No wait times recorded</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="analytics-section">
        <h3>Visits</h3>
        <ul className="ehist-list">
          {visible.map((row) => (
            <VisitCard key={row.activity.id} row={row} />
          ))}
        </ul>
        {rows.length > VISIBLE && (
          <button type="button" className="btn btn-small btn-secondary" onClick={() => setShowAll((s) => !s)}>
            {showAll ? "Show fewer" : `Show all ${rows.length}`}
          </button>
        )}
      </div>
    </section>
  );
}
